// getProfileTdk.js
'use strict';

import { ProfileListType } from '../resource/profileConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { makeHash } from '../resource/makeHash.js';

// Content-neutral CTA variants. The variant is picked by hashing the creator id
// only, so the same creator always keeps the same sentence, in every language
// (JSON-LD builds the description once per language).
const PROFILE_DESCRIPTION_CTA_KEYS = [
  'seo_meta_template_description_profile_cta_1',
  'seo_meta_template_description_profile_cta_2',
  'seo_meta_template_description_profile_cta_3',
  'seo_meta_template_description_profile_cta_4',
  'seo_meta_template_description_profile_cta_5',
];

// Hard cap of the biography segment, counted in code points so a surrogate
// pair (emoji) is never cut in half. A truncated segment ends in an ellipsis
// that counts towards the cap, so the segment is never longer than this. Deliberately not `BIOGRAPHY_MAXIMUM_LENGTH`
// from `profileConstants.js`: that is the profile editor's input limit which
// happens to share the value, while this is an SEO copy policy that has to be
// able to move on its own.
const BIOGRAPHY_SEGMENT_MAXIMUM_LENGTH = 150;
const BIOGRAPHY_SEGMENT_MAXIMUM_SENTENCE_COUNT = 2;

const EMAIL_PATTERN = /[\w.+-]+@[a-z\d-]+(?:\.[a-z\d-]+)+/gi;
// A deliberately bounded bare-domain allowlist keeps ordinary profile prose
// such as `身高160.5/體重45` from being mistaken for a link. Scheme and `www.`
// links do not need an allowlist.
const EXTERNAL_LINK_TOP_LEVEL_DOMAIN_SOURCE =
  '(?:com|net|org|io|me|tv|co|cc|link|live|app|xyz|gg|fans|fan|shop|store|site|' +
  'space|online|club|info|bio|page|blog|art|one|vip|pro|dev|ee|st|gl|im|is|sh|' +
  'to|ly|be|at|id|pe|tw|jp|kr|hk|cn|us|uk|ph|sg|my|th|vn|in|de|fr|es|it|nl|se|pl|' +
  'br|mx|ca|au|nz)';
const SCHEME_OR_WWW_LINK_PATTERN = /(?:https?:\/\/|www\.)\S+/gi;
const EXTERNAL_LINK_TOP_LEVEL_DOMAIN_PATTERN = new RegExp(
  `^${EXTERNAL_LINK_TOP_LEVEL_DOMAIN_SOURCE}$`,
  'i'
);
// Find a domain-shaped token without relying on ASCII word boundaries. Whether
// it is risky is decided separately, so `Mr.Wang` can remain prose while an IDN
// such as `例子.台灣` is still recognised.
const BARE_DOMAIN_CANDIDATE_PATTERN = new RegExp(
  '(?:[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]*[\\p{L}\\p{N}])?\\.)+' +
    '(\\p{L}[\\p{L}\\p{N}-]*[\\p{L}\\p{N}])(\\/\\S*)?',
  'giu'
);
// A contact only counts as one when the platform is followed by an `id`
// keyword or a separator, so wording like `drop me a line here` is kept.
const CONTACT_PLATFORM_SOURCE =
  '(?:line|ig|instagram|telegram|tg|wechat|weixin|whatsapp|twitter|onlyfans|' +
  'discord|snapchat|snap|kakao|kik|viber|zalo|skype|signal|potato|facebook|fb|' +
  'threads|tiktok|youtube|reddit|tumblr|weibo|qq|x)';
const LOCALIZED_CONTACT_LABEL_SOURCE = '(?:帳號|微信|私訊|加我|推特)';
// A platform followed by an id, or a generic `ID`/`帳號`/`私訊` label followed by
// one. `drop me a line here` has neither, so it survives.
const CONTACT_PATTERN = new RegExp(
  `\\b${CONTACT_PLATFORM_SOURCE}\\b\\s*(?:(?:id)?\\s*[:：=＝@＠]|id)\\s*[\\w.-]{2,}` +
    `|(?:\\bid\\b|${LOCALIZED_CONTACT_LABEL_SOURCE})\\s*[:：=＝@＠]?\\s*[\\w.-]{3,}`,
  'gi'
);
const MENTION_PATTERN = /@[\w.]{2,}/g;
// A phone number in a biography must never reach a public description. The run
// has to be phone shaped — a `+` country code or a leading `0`, then 8 or more
// digits and separators — so a measurement (`身高 168`), a price (`1000 元`), a
// date (`2026-08-19`) or broadcast hours (`09:00-18:00`) are left alone.
const PHONE_PATTERN = /(?:\+|\b0)[\d\s().-]{7,17}\d/g;
const WHITESPACE_PATTERN = /\s+/g;
const SEPARATOR_CHARACTER_SOURCE = '[,，、;；:：|/~]';
const EDGE_SEPARATOR_PATTERN = new RegExp(
  `^(?:\\s*${SEPARATOR_CHARACTER_SOURCE})+\\s*` +
    `|\\s*(?:${SEPARATOR_CHARACTER_SOURCE}\\s*)+$`,
  'g'
);
// A single emoji, including variation selectors, skin tone modifiers and
// zero-width-joiner sequences, so a joined emoji stays intact.
const EMOJI_CLUSTER_SOURCE =
  '\\p{Extended_Pictographic}(?:\\uFE0F|\\p{Emoji_Modifier})*(?:\\u200D\\p{Extended_Pictographic}(?:\\uFE0F|\\p{Emoji_Modifier})*)*';
const CONSECUTIVE_EMOJI_PATTERN = new RegExp(
  `(${EMOJI_CLUSTER_SOURCE})(?:\\s*(?:${EMOJI_CLUSTER_SOURCE}))+`,
  'gu'
);

// Free-form profile text is not a reliable place to partially reconstruct safe
// prose. If the first paragraph contains any risky signal, omit that biography
// segment and let the description fall back to its fact and CTA segments.
const RISKY_BIOGRAPHY_PATTERNS = [
  EMAIL_PATTERN,
  SCHEME_OR_WWW_LINK_PATTERN,
  CONTACT_PATTERN,
  PHONE_PATTERN,
  MENTION_PATTERN,
];

const hasPatternMatch = ({ pattern, text }) => {
  pattern.lastIndex = 0;
  const hasMatch = pattern.test(text);
  pattern.lastIndex = 0;
  return hasMatch;
};

const hasBareDomainRisk = text => {
  BARE_DOMAIN_CANDIDATE_PATTERN.lastIndex = 0;
  let match = BARE_DOMAIN_CANDIDATE_PATTERN.exec(text);
  while (match) {
    const [, topLevelDomain, path] = match;
    if (
      path ||
      /[^\p{ASCII}]/u.test(topLevelDomain) ||
      /^xn--/i.test(topLevelDomain) ||
      EXTERNAL_LINK_TOP_LEVEL_DOMAIN_PATTERN.test(topLevelDomain)
    ) {
      BARE_DOMAIN_CANDIDATE_PATTERN.lastIndex = 0;
      return true;
    }
    match = BARE_DOMAIN_CANDIDATE_PATTERN.exec(text);
  }
  BARE_DOMAIN_CANDIDATE_PATTERN.lastIndex = 0;
  return false;
};

const hasRiskyBiographySignal = paragraph =>
  RISKY_BIOGRAPHY_PATTERNS.some(pattern =>
    hasPatternMatch({ pattern, text: paragraph })
  ) || hasBareDomainRisk(paragraph);

/**
 * Keep the leading sentences of a text, treating the trailing part without a
 * terminator as a sentence too.
 * @param {object} params
 * @param {string} params.text - single line text.
 * @param {number} params.maximumSentenceCount - how many sentences to keep.
 * @returns {string} The leading sentences.
 * @example
 * takeFirstSentences({ text: 'a。b。c。', maximumSentenceCount: 2 });
 * // => 'a。b。'
 */
const takeFirstSentences = ({ text, maximumSentenceCount }) => {
  // Built per call so the global regex never carries `lastIndex` over.
  const terminatorPattern = /[。！？…]+|[!?.]+(?=\s|$)/gu;
  const sentences = [];
  let startIndex = 0;
  let match = terminatorPattern.exec(text);
  while (match && sentences.length < maximumSentenceCount) {
    const endIndex = match.index + match[0].length;
    sentences.push(text.slice(startIndex, endIndex));
    startIndex = endIndex;
    match = terminatorPattern.exec(text);
  }
  if (sentences.length < maximumSentenceCount && startIndex < text.length) {
    sentences.push(text.slice(startIndex));
  }
  return sentences.join('').trim();
};

/**
 * Build the biography segment of the profile home description: the first
 * paragraph when it has no link or contact risk, with emoji spam collapsed, at
 * most a couple of sentences and never longer than the hard cap.
 * @param {object} params
 * @param {string} params.biography - user's biography.
 * @returns {string} The biography segment, empty when there is nothing to show.
 * @example
 * getBiographySegment({ biography: 'I love cats.\n\nfind me https://a.com' });
 * // => 'I love cats.'
 */
const getBiographySegment = ({ biography }) => {
  if (!biography) return '';

  // Biographies often open with blank lines, so the first paragraph that has
  // any text is the creator's own opening, not the empty one before it.
  const firstParagraph =
    String(biography)
      .split(/\r?\n\s*\r?\n/)
      .find(paragraph => paragraph.trim()) ?? '';
  if (hasRiskyBiographySignal(firstParagraph)) return '';

  const sanitizedFirstParagraph = firstParagraph
    .replace(CONSECUTIVE_EMOJI_PATTERN, '$1')
    .replace(WHITESPACE_PATTERN, ' ')
    .trim();

  const sentences = takeFirstSentences({
    text: sanitizedFirstParagraph,
    maximumSentenceCount: BIOGRAPHY_SEGMENT_MAXIMUM_SENTENCE_COUNT,
  });
  const codePoints = Array.from(sentences);

  if (codePoints.length <= BIOGRAPHY_SEGMENT_MAXIMUM_LENGTH) return sentences;

  // The ellipsis counts towards the cap, and a separator stranded by the cut
  // goes with it, so the segment never reads `…喜歡貓，…`.
  return `${codePoints
    .slice(0, BIOGRAPHY_SEGMENT_MAXIMUM_LENGTH - 1)
    .join('')
    .replace(EDGE_SEPARATOR_PATTERN, '')
    .trim()}…`;
};

/**
 * Build the profile home page description: fact segment, then the creator's
 * own biography, then a stable CTA. Missing data drops its own segment so the
 * description is never blank and never shows a placeholder value.
 * @param {object} params
 * @param {string} params.username - username, used as the handle.
 * @param {string} params.displayName - user's display name.
 * @param {string} params.biography - user's biography.
 * @param {number} params.postCount - user's post count.
 * @param {string} params.creatorId - user id, the CTA variant is hashed from it.
 * @param {function} params.t - i18n.t
 * @returns {string} The description.
 * @example
 * getProfileHomeDescription({ username: 'olivia', displayName: 'Olivia',
 *   biography: 'I love cats.', postCount: 6, creatorId: 'user-1', t });
 * // => '<fact sentence with name, handle and 6 posts> I love cats. <cta>'
 */
const getProfileHomeDescription = ({
  username,
  displayName,
  biography,
  postCount,
  creatorId,
  t,
}) => {
  const profileName = (displayName || '').trim() || (username || '').trim();
  // Falls back to the display name so the handle placeholder is never empty
  // nor `null`; on `/u/<handle>` the handle is always given.
  const profileHandle = (username || '').trim() || profileName;
  const normalizedPostCount = Number(postCount);
  const hasPostCount =
    Number.isFinite(normalizedPostCount) && normalizedPostCount > 0;

  const factSegment = !profileName
    ? ''
    : hasPostCount
      ? t('seo_meta_template_description_profile_fact', {
          cp_name: profileName,
          cp_handle: profileHandle,
          post_count: normalizedPostCount,
          ns: TranslationNamespace.SEO,
        })
      : t('seo_meta_template_description_profile_fact_no_post', {
          cp_name: profileName,
          cp_handle: profileHandle,
          ns: TranslationNamespace.SEO,
        });

  return [
    factSegment,
    getBiographySegment({ biography }),
    t(
      PROFILE_DESCRIPTION_CTA_KEYS[
        Math.abs(makeHash(String(creatorId || profileHandle))) %
          PROFILE_DESCRIPTION_CTA_KEYS.length
      ],
      { ns: TranslationNamespace.SEO }
    ),
  ]
    .filter(Boolean)
    .join(' ')
    .replace(WHITESPACE_PATTERN, ' ')
    .trim();
};

/**
 * Get profile tdk
 * @param {object} params
 * @param {string} params.username - username.
 * @param {string} params.displayName - user's display name.
 * @param {string} params.biography - user's biography.
 * @param {number} params.postCount - user's post count, for the home page description.
 * @param {string} params.creatorId - user id, for the home page CTA variant.
 * @param {string} params.listType - ex: ProfileListType.FLIX, empty on the home page.
 * @param {function} params.t - i18n.t
 * @returns {{displayTitle: string, displayDescription: string, displayKeywords: string}} The tdk.
 * @example
 * getProfileTdk({ username: 'olivia', displayName: 'Olivia',
 *   biography: 'I love cats.', postCount: 6, creatorId: 'user-1', t });
 * // => { displayTitle: '...', displayDescription: '...', displayKeywords: '...' }
 */
const getProfileTdk = ({
  username,
  displayName,
  biography,
  postCount,
  creatorId,
  listType,
  t,
}) => {
  let displayTitle;
  let displayDescription;
  const displayKeywords = t('seo_cp_keywords', {
    cp_name: username,
    ns: TranslationNamespace.SEO,
  });

  switch (listType) {
    case ProfileListType.FLIX: {
      displayTitle = t('seo_cp_profile_video_title', {
        cp_name: username,
        cp_profile: biography,
        page_type: t('title_flix', {
          ns: TranslationNamespace.PROFILE,
        }),
        ns: TranslationNamespace.SEO,
      });
      displayDescription = t('seo_cp_profile_video_description', {
        cp_name: username,
        cp_profile: biography,
        page_type: t('title_flix', {
          ns: TranslationNamespace.PROFILE,
        }),
        ns: TranslationNamespace.SEO,
      });
      break;
    }
    case ProfileListType.IMAGE: {
      displayTitle = t('seo_cp_profile_image_title', {
        cp_name: username,
        cp_profile: biography,
        page_type: t('profile_photo', {
          ns: TranslationNamespace.PROFILE,
        }),
        ns: TranslationNamespace.SEO,
      });
      displayDescription = t('seo_cp_profile_image_description', {
        cp_name: username,
        cp_profile: biography,
        page_type: t('profile_photo', {
          ns: TranslationNamespace.PROFILE,
        }),
        ns: TranslationNamespace.SEO,
      });
      break;
    }
    case ProfileListType.POST:
    case ProfileListType.STORY:
    case ProfileListType.HOME:
    default: {
      const pageType = t(
        ProfileListType.HOME === listType
          ? 'home_page'
          : ProfileListType.POST === listType
            ? 'post_title'
            : 'broadcast',
        {
          ns: TranslationNamespace.PROFILE,
        }
      );
      displayTitle = t('seo_cp_title', {
        cp_name: username,
        cp_profile: biography,
        page_type: pageType,
        ns: TranslationNamespace.SEO,
      });
      // `/u/<handle>` has no list type, so an absent list type is the home
      // page. Only the home page gets the creator-specific description, tab
      // pages keep the shared template.
      displayDescription =
        !listType || ProfileListType.HOME === listType
          ? getProfileHomeDescription({
              username,
              displayName,
              biography,
              postCount,
              creatorId,
              t,
            })
          : t('seo_cp_description', {
              cp_name: username,
              cp_profile: biography,
              page_type: pageType,
              ns: TranslationNamespace.SEO,
            });
      break;
    }
  }

  return { displayTitle, displayDescription, displayKeywords };
};

export default getProfileTdk;
